import { Info, Loader2, Tag } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Field } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { confirmModal, Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { apiErrorMessage } from '@/i18n'
import { isApiError } from '@/lib/api/errors'
import { cn } from '@/lib/utils'
import { useModuleLinks, useSaveCatalogModule } from '../../catalogApi'
import type { CatalogModuleView, ModuleRequirement, ProjectSummary } from '../../types'
import { ProjectIcon } from '../ProjectBits'

type Row = { projectId: string; on: boolean; requirement: ModuleRequirement; price: string }
const PRICE = /^\d+(\.\d{1,2})?$/

export function ModulePricingModal({ module: m, projects, onClose }: { module: CatalogModuleView; projects: ProjectSummary[]; onClose: () => void }) {
  const { t } = useTranslation()
  const saveModule = useSaveCatalogModule()
  const { integrate, update, unlink } = useModuleLinks()
  const [listPrice, setListPrice] = useState(m.price)
  const initial: Row[] = projects.map((p) => {
    const u = m.usage.find((x) => x.projectId === p.id)
    return { projectId: p.id, on: !!u, requirement: u?.requirement ?? 'optional', price: u?.price ?? m.price }
  })
  const [rows, setRows] = useState(initial)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const set = (projectId: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.projectId === projectId ? { ...r, ...patch } : r)))
  const nameOf = (id: string) => projects.find((p) => p.id === id)?.name ?? id

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!PRICE.test(listPrice) || rows.some((r) => r.on && !PRICE.test(r.price))) {
      setError(t('projects.catalog.form.priceInvalid'))
      return
    }
    const before = new Map(initial.map((r) => [r.projectId, r]))
    const added = rows.filter((r) => r.on && !before.get(r.projectId)?.on)
    const removed = rows.filter((r) => !r.on && before.get(r.projectId)?.on)
    const changed = rows.filter((r) => {
      const b = before.get(r.projectId)
      return r.on && b?.on && (b.requirement !== r.requirement || b.price !== r.price)
    })
    if (removed.length) {
      const ok = await confirmModal({
        title: t('projects.catalog.pricing.removeTitle'),
        description: t('projects.catalog.pricing.removeBody', { module: m.name, projects: removed.map((r) => nameOf(r.projectId)).join(', ') }),
        confirmLabel: t('projects.modules.remove'),
        destructive: true,
      })
      if (!ok) return
    }
    setPending(true)
    const failures: string[] = []
    const run = async (label: string, fn: () => Promise<unknown>) => {
      try {
        await fn()
      } catch (err) {
        const names = isApiError(err) ? (err.details as { modules?: string[] } | undefined)?.modules : undefined
        failures.push(`${label}: ${apiErrorMessage(err)}${names ? ` (${names.join(', ')})` : ''}`)
      }
    }
    if (listPrice !== m.price) {
      const { id: _id, features: _f, changelog: _c, updatedAt: _u, usage: _us, ...input } = m
      await run(t('projects.catalog.priceList.listPrice'), () => saveModule.mutateAsync({ id: m.id, input: { ...input, price: listPrice } }))
    }
    for (const r of added) await run(nameOf(r.projectId), () => integrate.mutateAsync({ projectId: r.projectId, moduleId: m.id, input: { requirement: r.requirement, price: r.price } }))
    for (const r of changed) await run(nameOf(r.projectId), () => update.mutateAsync({ projectId: r.projectId, moduleId: m.id, input: { requirement: r.requirement, price: r.price } }))
    for (const r of removed) await run(nameOf(r.projectId), () => unlink.mutateAsync({ projectId: r.projectId, moduleId: m.id }))
    setPending(false)
    if (failures.length) {
      toast.error(t('projects.catalog.pricing.partial'), { description: failures.join(' · ') })
      return
    }
    toast.success(t('common.saved'))
    onClose()
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onClose()}
      dismissible={!pending}
      icon={<Tag />}
      title={t('projects.catalog.pricing.title', { name: m.name })}
      description={`${m.code} · ${t(`projects.pricing.${m.pricing}`)}`}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <Field id="mp-list" label={t('projects.catalog.priceList.listPrice')} hint={t('projects.catalog.pricing.listHint')}>
          <NumberInput id="mp-list" value={listPrice} onChange={setListPrice} minDecimalsOnBlur={2} suffix="₺" className="max-w-48" />
        </Field>

        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">{t('projects.catalog.pricing.perProduct')}</legend>
          {rows.map((r) => {
            const p = projects.find((x) => x.id === r.projectId)!
            const lockedOff = !r.on && !m.active
            return (
              <div key={r.projectId} className={cn('grid grid-cols-1 items-center gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1fr)_auto_10rem]', r.on ? 'bg-card' : 'bg-muted/30')}>
                <label className={cn('flex items-center gap-2.5', lockedOff ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
                  <input type="checkbox" className="size-4 accent-[var(--color-primary)]" checked={r.on} disabled={lockedOff} onChange={(e) => set(r.projectId, { on: e.target.checked })} />
                  <ProjectIcon icon={p.icon} color={p.color} className="size-7 rounded-md shadow-none" />
                  <span className="min-w-0">
                    <span className="block font-medium">{p.name}</span>
                    <span className="block text-xs text-muted-foreground">{r.on ? t('projects.catalog.pricing.offered') : lockedOff ? t('projects.catalog.pricing.inactive') : t('projects.catalog.pricing.notOffered')}</span>
                  </span>
                </label>
                <div role="radiogroup" aria-label={t('projects.catalog.pricing.requirement', { project: p.name })} className={cn('inline-flex rounded-lg border p-0.5 text-xs', !r.on && 'pointer-events-none opacity-40')}>
                  {(['required', 'optional'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      role="radio"
                      aria-checked={r.requirement === k}
                      disabled={!r.on}
                      onClick={() => set(r.projectId, { requirement: k })}
                      className={cn('rounded-md px-2.5 py-1 font-medium transition-colors', r.requirement === k ? (k === 'required' ? 'bg-success/15 text-success' : 'bg-info/12 text-info') : 'text-muted-foreground hover:text-foreground')}
                    >
                      {t(`projects.requirement.${k}`)}
                    </button>
                  ))}
                </div>
                <NumberInput
                  aria-label={t('projects.catalog.pricing.priceFor', { project: p.name })}
                  value={r.price}
                  onChange={(v) => set(r.projectId, { price: v })}
                  minDecimalsOnBlur={2}
                  suffix="₺"
                  disabled={!r.on}
                />
              </div>
            )
          })}
        </fieldset>

        {m.dependsOn.length ? (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {t('projects.catalog.pricing.depsHint')}
          </p>
        ) : null}
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    </Modal>
  )
}
